const SKIP_BELOW_BYTES = 700 * 1024
const MAX_DIMENSION = 1600

/**
 * Shrinks a large screenshot (re-encoded as JPEG, longest side 1600px) so it
 * uploads quickly and stays well under server limits. Small files and GIFs are
 * returned untouched, as is anything the browser can't decode.
 */
export async function shrinkImage(file: File): Promise<File> {
  if (file.size <= SKIP_BELOW_BYTES || file.type === "image/gif" || typeof createImageBitmap === "undefined") return file

  try {
    const bitmap = await createImageBitmap(file)
    const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height))
    const canvas = document.createElement("canvas")
    canvas.width = Math.round(bitmap.width * scale)
    canvas.height = Math.round(bitmap.height * scale)
    const context = canvas.getContext("2d")
    if (!context) return file
    // JPEG has no transparency; paint white first so transparent PNGs don't turn black.
    context.fillStyle = "#fff"
    context.fillRect(0, 0, canvas.width, canvas.height)
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.85))
    if (!blob || blob.size >= file.size) return file
    return new File([blob], file.name.replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" })
  } catch {
    return file
  }
}
